/* ------------------------------------------------------------------ *\
JSON Static blocks - generic code to easily replace
loops by json static.
Example HTML:

<div
    class="js-json-static"
    data-url="#$json|key-for-json$#"
    data-filters="limit=5,is_featured=1"
>
    <script type="text/template">
        <% if (items && items.length) { %>
            <% items.forEach(function(item){ %>
                <div><%= item.id %> - <%= item.name %></div>
            <% }) %>
        <% } %>
    </script>
</div>

You can add the data-attribute data-remove-empty="true"
to remove the whole "js-json-static" element if needed

\* ------------------------------------------------------------ */

import ejs from 'ejs';
const GalaxyHelpers = window.GalaxyHelpers;

const debug = true; // activate debug during development, then you can put it to false

/**
 * Transforms data-filters string into object of filters
 * Ex: data-filters="is_featured=1,limit=5"
 * will become { is_featured: 1, limit: 5}
 *
 * Ex: data-filters="__ids_categories=1 3 5 39"
 * will become { __ids_categories: [1,3,5,39] }

 *
 * @param {string} [filterString='']
 * @return {object} Object representing the filters with key: value
 */
const getFilters = (filterString = '') => {
    return filterString.split(',').reduce((filters, currentValue) => {
        const [ name, value ] = currentValue.split('=');

        if (name && value) {
            const filterName = name.trim();
            const filterValue = value.trim()

            if (value.indexOf(' ') < 0 ) {
                filters[filterName] = filterValue;
            } else {
                // for multiple categories filter
                filters[filterName] = filterValue.split(' ').map(val => parseInt(val)).filter(val => !isNaN(val));
            }
        }

        return filters;
    }, {});
};

/**
 * @callback callbackFunction
 * @param  {HTMLElement} el - Current element
 * @param  {object[]} data - Loaded data for this element
 */

/**
 * JSON Static blocks - generic code to easily replace
 * loops by json static for the element with "js-json-static" class
 *
 * The callback function will be called for each
 * .js-json-static replaced elements
 *
 * @param {object} [config={}] configuration object
 * @param {callbackFunction} config.callback callback function
 * @param {HTMLElement} [config.container] optional container. If used, only the .js-json-static inside the container will be replaced
 */
export default function (config = {}) {
    const callbackFunction = config.callback || false;

    const containerElement = config.container || document;
    containerElement.querySelectorAll('.js-json-static').forEach(el => {
        const $el = $(el);

        if (['loading','done'].indexOf(el.dataset.status) > -1) {
            console.log('[[json-static]] Element already loading/done', $el);
            return;
        }

        const url = el.dataset.url;
        const filters = getFilters(el.dataset.filters);
        const errors = [];

        const templateElements = el.querySelectorAll('script[type="text/template"]');

        if (!url) {
            errors.push('data-url missing');
        }

        if (templateElements.length < 1) {
            errors.push('script[type="text/template"] tag missing');
        }

        if (errors.length) {
            errors.forEach(text => console.warn(text));
            return;
        }

        el.dataset.status = 'loading';

        GalaxyHelpers.staticSearch({
            debug: debug,
            url,
            filters,
        }).then(data => {
            const { results, count } = data;

            /* Replace the different script[type="text/template"] tags
            by the compiled code */
            templateElements.forEach( element => {
                try {
                    const sourceHTML = element.innerHTML;
                    const template = ejs.compile(sourceHTML);

                    const compiledHtml = template({
                        error: false,
                        items: results
                    });

                    element.insertAdjacentHTML('beforebegin', compiledHtml);
                    element.parentNode.removeChild(element);
                } catch (error) {
                    console.error(error);
                }
            });

            if (el.dataset['remove-empty'] && count === 0) {
                if (debug) console.log('json-static - Removing empty element', el);
                el.parentNode.removeChild(el);
            }

            el.dataset.status = 'done';

            if (typeof callbackFunction === 'function') {
                callbackFunction(el, data);
            }
        });

    });
}
